import React from 'react';
import i18next from 'i18next';
import {Style} from '@capacitor/status-bar';
import {Box, Notice, Stack, Text} from 'modern-famly';
import {useDispatch} from 'react-redux';

import LocalStorageService from 'web-app/services/local-storage';
import Button from 'web-app/react/components/form-elements/button/button';
import {showDialog, DialogChoice} from 'web-app/react/dialog';
import Link from 'web-app/react/components/link/link';
import {ACCESS_TOKEN_KEY} from 'web-app/util/access-token-helpers';
import PlatformHelper from 'web-app/helpers/platform-helper';
import * as CapacitorUtil from 'web-app/util/capacitor';
import {navigationContext} from 'web-app/react/contexts/navigation';
import useTypedSelector from 'web-app/react/hooks/use-typed-selector';
import ApiHelper from 'web-app/services/api-helper';

import {type ExistingLogin, type LoginFormData} from './models';
import {LoginContainer} from './shared-components';
import * as Actions from './actions';
import * as Constants from './constants';
import LoginForm from './login-form';
import PIN from './login-pin';
import AccountSelector from './account-selector';
import LoginError from './login-error';
import LoginChrome from './login-chrome';
import * as Selectors from './selectors';
import ChallengeContextWrapper from './challenge-context-wrapper';
import {LoginTermsAndConditions} from './login-terms-and-conditions';
import {isValidEmail} from './helpers';

interface InputProps {
    redirectUrl?: string;
    email?: string;
    locale?: string;
    isExistingUserSelfSignup?: boolean;
}

const LoginComponent = (props: InputProps) => {
    React.useEffect(() => {
        CapacitorUtil.setStatusBar(Style.Light);

        return () => {
            CapacitorUtil.setStatusBar(Style.Dark);
        };
    }, []);

    const dispatch = useDispatch();

    React.useEffect(() => {
        // no-op if accessToken exists - the LoginRedirectHandler component will take over from that point
        if (!LocalStorageService.getFromLocalStorage(ACCESS_TOKEN_KEY)) {
            const deviceId = LocalStorageService.getFromLocalStorage('deviceId');
            if (deviceId) {
                dispatch(Actions.fetchLoginsForDevice.action(deviceId));
            }
        }

        return () => {
            dispatch(Actions.reset.action());
        };
    }, [dispatch]);

    const locale = useTypedSelector(state => Selectors.locale(state, {locale: props.locale}));
    const errorState = useTypedSelector(state => state.login.errorState);
    const forceCredentials = useTypedSelector(state => state.login.forceCredentials);
    const selectedAccountId = useTypedSelector(state => state.login.selectedAccountId);
    const loadingDeviceAccounts = useTypedSelector(state => state.login.loadingDeviceAccounts);
    const emailForSelectedAccount = useTypedSelector(state => Selectors.emailForSelectedAccount(state));
    const nameForSelectedAccount = useTypedSelector(state => Selectors.nameForSelectedAccount(state));
    const emailFormForm = useTypedSelector(state => Selectors.loginEmail(state));
    const hasSavedAccounts = useTypedSelector(state => Selectors.hasSavedAccounts(state));
    const shouldShowError = useTypedSelector(state => Selectors.shouldShowError(state));
    const showMagicLinkOption = useTypedSelector(state => Selectors.showMagicLinkOption(state));
    const loginForMagicLink = useTypedSelector(state => Selectors.loginForMagicLink(state));
    const magicLinkSent = useTypedSelector(state => Selectors.magicLinkSent(state));
    const initialEmail = useTypedSelector(state => Selectors.initialLoginFormEmailValue(state, {email: props.email}));
    const challengeContext = useTypedSelector(state => state.login.challengeContext);
    const shouldUseUserContext = useTypedSelector(state => Selectors.shouldUseUserContext(state));
    const shouldUseUserContextForSelectedAccount = useTypedSelector(state =>
        Selectors.shouldUseUserContextForSelectedAccount(state),
    );
    const oneTimePasswordConfiguration = useTypedSelector(state =>
        Selectors.getOneTimePasswordConfiguration(state, {scope: 'login'}),
    );

    React.useEffect(() => {
        const email = initialEmail;
        if (isValidEmail(email)) {
            dispatch(Actions.testEmailForLogins.action(email));
        }
    }, [initialEmail, dispatch]);

    const showCredentialForm = selectedAccountId === Constants.NEW_ACCOUNT_IDENTIFIER || forceCredentials;

    const redirectUrl = React.useMemo(() => {
        if (!props.redirectUrl) {
            return undefined;
        }

        try {
            const urlToRedirectTo = new URL(props.redirectUrl);

            // If the host is specified, we want only redirect to urls that shares the same host as the current.
            if (!urlToRedirectTo.host || urlToRedirectTo.host === window.location.host) {
                return props.redirectUrl;
            } else {
                return undefined;
            }
        } catch {
            return undefined;
        }
    }, [props.redirectUrl]);

    const handlePINSubmit = React.useCallback(
        (pin: string) => {
            dispatch(
                Actions.pinLogin.action(
                    pin,
                    selectedAccountId,
                    LocalStorageService.getFromLocalStorage('deviceId'),
                    {redirectUrl},
                    shouldUseUserContextForSelectedAccount,
                ),
            );
        },
        [selectedAccountId, redirectUrl, shouldUseUserContextForSelectedAccount, dispatch],
    );

    const handleMagicLinkSubmit = React.useCallback(
        (data: LoginFormData, login?: ExistingLogin | null) => {
            dispatch(Actions.magicLinkLogin.action({...data}, login));
        },
        [dispatch],
    );

    const {generateHash, transitionToPath} = React.useContext(navigationContext);

    const handleLoginSubmit = React.useCallback(
        (data: LoginFormData) => {
            // If the user can use a one-time password, we should redirect to the reset password page
            // instead of logging in.
            if (oneTimePasswordConfiguration.canUseOneTimePassword) {
                // If we are in the mobile app, we need to set the API key to the one from the /exists result.
                // Otherwise, the /welcome route will not work.
                if (PlatformHelper.isMobileApp()) {
                    ApiHelper.setApiConfig(oneTimePasswordConfiguration.apiKey);
                }

                transitionToPath('/welcome/:token', {
                    token: data.password,
                    queryParams: {
                        email: data.email,
                    },
                });

                return;
            }

            if (showMagicLinkOption) {
                handleMagicLinkSubmit(data, PlatformHelper.isMobileApp() ? loginForMagicLink : null);
                return;
            }

            if (PlatformHelper.isMobileApp()) {
                let formattedLoginTLD;
                // If the user has selected an option from the login form (in case there are multiple available options to choose from)
                if (data.loginTLD) {
                    const split = data.loginTLD.split('.');
                    formattedLoginTLD = `.${split[split.length - 1]}`;
                }

                dispatch(
                    Actions.credentialLogin.action(
                        {
                            ...data,
                            loginTLD: formattedLoginTLD,
                            shouldUseUserContext,
                            // We should ping all servers if we're on mobile and we don't know the loginTLD in advance
                            shouldPingAllServers: !formattedLoginTLD,
                        },
                        {
                            redirectUrl,
                        },
                    ),
                );
            } else {
                dispatch(
                    Actions.credentialLogin.action(
                        {
                            ...data,
                            loginTLD: undefined,
                            shouldUseUserContext,
                        },
                        {
                            redirectUrl,
                        },
                    ),
                );
            }
        },
        [
            oneTimePasswordConfiguration.canUseOneTimePassword,
            oneTimePasswordConfiguration.apiKey,
            showMagicLinkOption,
            transitionToPath,
            handleMagicLinkSubmit,
            loginForMagicLink,
            dispatch,
            shouldUseUserContext,
            redirectUrl,
        ],
    );

    const handleAccountSelect = React.useCallback(
        (loginId: string) => {
            dispatch(Actions.selectAccount.action(loginId));
            LocalStorageService.saveToLocalStorage(Constants.SELECTED_ACCOUNT_LOCAL_STORAGE_KEY, loginId);
        },
        [dispatch],
    );

    const handleRemoveAccount = React.useCallback(() => {
        showDialog({
            title: i18next.t('login.hideAccountTitle', {name: nameForSelectedAccount}),
            description: i18next.t('login.confirmHideAccount', {name: nameForSelectedAccount}),
            okText: i18next.t('login.acceptHideAccount'),
            cancelText: i18next.t('no'),
        }).then(selection => {
            if (selection === DialogChoice.OK) {
                dispatch(
                    Actions.removeAccount.action(
                        LocalStorageService.getFromLocalStorage('deviceId'),
                        selectedAccountId,
                    ),
                );
            }
        });
    }, [dispatch, nameForSelectedAccount, selectedAccountId]);

    const handleToggleForceCredentials = React.useCallback(() => {
        dispatch(Actions.setForceCredentials.action(!forceCredentials));
    }, [dispatch, forceCredentials]);

    const handleCancelChallenge = React.useCallback(() => {
        dispatch(Actions.resetChallenge.action());
    }, [dispatch]);

    return (
        <>
            <LoginChrome
                locale={locale}
                hasAccounts={loadingDeviceAccounts ? undefined : hasSavedAccounts}
                loadingDeviceAccounts={loadingDeviceAccounts}
            >
                {loadingDeviceAccounts ? null : (
                    <>
                        {!showMagicLinkOption ? <AccountSelector onAccountSelect={handleAccountSelect} /> : null}
                        <LoginContainer data-e2e-id="login-container" wide={showCredentialForm} data-sentry-block>
                            {props.isExistingUserSelfSignup ? (
                                <Box marginBottom={6}>
                                    <Notice text={i18next.t('selfSignup.existingUser.loginNotice')} severity="info" />
                                </Box>
                            ) : null}
                            {errorState && shouldShowError ? (
                                <LoginError error={errorState} email={emailFormForm} />
                            ) : null}
                            {showCredentialForm ? (
                                <>
                                    <LoginForm
                                        onSubmit={handleLoginSubmit}
                                        email={emailForSelectedAccount || props.email}
                                        errorState={errorState}
                                        showMagicLinkOption={showMagicLinkOption}
                                        magicLinkSent={magicLinkSent}
                                        locale={locale}
                                    />
                                    {forceCredentials ? (
                                        <Stack justifyContent="center" marginTop={8}>
                                            <Button appearance="none" onClick={handleToggleForceCredentials}>
                                                {i18next.t('login.backToPincode')}
                                            </Button>
                                        </Stack>
                                    ) : (
                                        <>
                                            {!showMagicLinkOption ? (
                                                <Stack flexDirection="column" textAlign="center" marginTop={8}>
                                                    <Link href={generateHash('/newAccount')}>
                                                        <Text variant="body" color="n300">
                                                            {i18next.t('login.dontHaveAnAccount')}
                                                        </Text>
                                                    </Link>
                                                    {/**
                                                     * Only Google has asked us to show a link to our Terms and Policies so
                                                     * this is the only case we'll show it. Furthermore, having this be
                                                     * directly under "Don't have an account?" has lead to a lot of confusion
                                                     * which is the main motivation for hiding this on web and iOS.
                                                     **/}
                                                    {PlatformHelper.isAndroid() ? (
                                                        <>
                                                            <Text variant="body" color="n300">
                                                                &middot;
                                                            </Text>
                                                            <LoginTermsAndConditions />
                                                        </>
                                                    ) : null}
                                                </Stack>
                                            ) : null}
                                        </>
                                    )}
                                </>
                            ) : (
                                <>
                                    <PIN onSubmit={handlePINSubmit} onAccountSelect={handleAccountSelect} />
                                    <Stack justifyContent="space-between" marginTop={4}>
                                        <Button appearance="none" onClick={handleToggleForceCredentials}>
                                            <Text variant="body" color="n300">
                                                {i18next.t('login.forgotPincode')}
                                            </Text>
                                        </Button>
                                        <Button id="removeAccount" appearance="none" onClick={handleRemoveAccount}>
                                            <Box textAlign="right">
                                                <Text variant="body" color="n300">
                                                    {i18next.t('login.hideAccount')}
                                                </Text>
                                            </Box>
                                        </Button>
                                    </Stack>
                                </>
                            )}
                        </LoginContainer>
                    </>
                )}
            </LoginChrome>
            {challengeContext.show ? (
                <ChallengeContextWrapper
                    challengeContext={challengeContext.challenge}
                    onCancel={handleCancelChallenge}
                    redirectUrl={redirectUrl}
                    loginTLD={challengeContext.loginTLD}
                    email={emailFormForm ?? ''}
                />
            ) : null}
        </>
    );
};

export default LoginComponent;
